import { Group, Text, Button, SimpleGrid, Paper, Stack } from '@mantine/core';
import { Settings, Download } from 'react-feather';
import { formatCentsCompact } from '../../../../utils/formatters';

interface CompensationHeaderProps {
  audienceTypeLabel: string;
  sessionDuration: number | null;
  isConfirmed: boolean;
  totalEstimatedCents?: number;
  assignedTotalEstimatedCents?: number;
  potentialTotalEstimatedCents?: number;
  systemCalculatedCount?: number;
  confirmedCount?: number;
  pendingCount?: number;
  coachCount?: number;
  /** Net-10 payments not yet overdue. */
  paymentsDueCount?: number;
  paymentsOverdueCount?: number;
  /** Sum of all Net-10 payments awaiting submission, overdue included. */
  paymentsDueCents?: number;
  hasCalculation?: boolean;
  onConfigure: () => void;
  onUnlock: () => void;
  onExportCsv?: () => void;
}

interface StatCardProps {
  label: string;
  value: string;
  subtext?: string;
  subtextColor?: string;
}

function StatCard({ label, value, subtext, subtextColor }: StatCardProps) {
  return (
    <Paper
      p="md"
      radius="md"
      style={{ border: '1px solid #E8E6E1', background: 'white' }}
    >
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: 0.3 }}>
        {label}
      </Text>
      <Text fw={700} size="xl" mt={4}>
        {value}
      </Text>
      {subtext && (
        <Text size="xs" c={subtextColor ?? 'dimmed'} mt={2}>
          {subtext}
        </Text>
      )}
    </Paper>
  );
}

export function CompensationHeader({
  audienceTypeLabel,
  sessionDuration,
  isConfirmed,
  totalEstimatedCents = 0,
  assignedTotalEstimatedCents = 0,
  potentialTotalEstimatedCents = 0,
  systemCalculatedCount = 0,
  confirmedCount = 0,
  pendingCount = 0,
  coachCount = 0,
  paymentsDueCount = 0,
  paymentsOverdueCount = 0,
  paymentsDueCents = 0,
  hasCalculation = false,
  onConfigure,
  onUnlock,
  onExportCsv,
}: CompensationHeaderProps) {
  const subtitle = [
    audienceTypeLabel,
    sessionDuration ? `${sessionDuration} min sessions` : null,
  ]
    .filter(Boolean)
    .join(' \u00B7 ');

  const totalCoaches = systemCalculatedCount + confirmedCount + pendingCount;
  const avgPerCoach = coachCount > 0 ? Math.round(totalEstimatedCents / coachCount) : 0;

  return (
    <Stack gap="md">
      {/* 4 summary cards (AC1) */}
      {hasCalculation && (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
          <StatCard
            label="Total Cohort Compensation"
            value={formatCentsCompact(totalEstimatedCents)}
            subtext={
              potentialTotalEstimatedCents > 0
                ? `${formatCentsCompact(assignedTotalEstimatedCents)} assigned · ${formatCentsCompact(potentialTotalEstimatedCents)} estimated`
                : `${coachCount} ${coachCount === 1 ? 'coach' : 'coaches'}`
            }
          />
          <StatCard
            label="Confirmed"
            value={`${confirmedCount}/${totalCoaches}`}
            subtext={`${systemCalculatedCount} calculated${pendingCount > 0 ? `, ${pendingCount} pending` : ''}`}
          />
          <StatCard
            label="Avg Per Coach"
            value={formatCentsCompact(avgPerCoach)}
            subtext="Estimated"
          />
          <StatCard
            label="Payments Due"
            value={formatCentsCompact(paymentsDueCents)}
            subtext={
              paymentsOverdueCount > 0
                ? `${paymentsOverdueCount} overdue${paymentsDueCount > 0 ? `, ${paymentsDueCount} pending` : ''}`
                : `${paymentsDueCount} pending`
            }
            subtextColor={paymentsOverdueCount > 0 ? 'dangerRed' : 'dimmed'}
          />
        </SimpleGrid>
      )}

      {/* Header row with title, metadata, and action buttons */}
      <Group
        justify="space-between"
        align="center"
        p="md"
        style={{ border: '1px solid #E8E6E1', borderRadius: 8, background: 'white' }}
      >
        <div>
          <Text fw={700} size="lg">
            Coach Compensation
          </Text>
          <Text size="sm" c="dimmed" mt={2}>
            {subtitle}
          </Text>
          {hasCalculation && systemCalculatedCount > 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              Review each coach's breakdown. Confirm if correct, or adjust if needed.
            </Text>
          )}
        </div>
        <Group gap="xs">
          <Button
            variant="outline"
            color="gray"
            bg="white"
            leftSection={<Settings size={16} />}
            onClick={isConfirmed ? onUnlock : onConfigure}
          >
            {isConfirmed ? 'Unlock' : 'Configure'}
          </Button>
          <Button
            variant="outline"
            color="gray"
            bg="white"
            leftSection={<Download size={16} />}
            onClick={onExportCsv}
            disabled={!hasCalculation || !onExportCsv}
          >
            Export CSV
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}

